import { Head, router, usePage } from '@inertiajs/react';
import { useMemo, useState } from 'react';
import { User, Stethoscope } from 'lucide-react';
import AuthLayout from '@/components/auth/AuthLayout';
import LoginForm from '@/components/auth/LoginForm';

type PageProps = {
  defaultRole: 'patient' | 'therapist';
  submitUrls: { patient: string; therapist: string };
  errors?: Record<string, string>;
  flash?: { notice?: string; alert?: string; success?: string; error?: string };
  authError?: string;
  lastEmail?: string;
};

export default function Login() {
  const { props } = usePage<PageProps>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const role = props.defaultRole;
  const submitUrl = useMemo(() => props.submitUrls[role], [props.submitUrls, role]);

  const flash = props.flash ?? null;
  const authError = props.authError || flash?.alert || null;
  const flashNotice = flash?.notice ?? null;
  const flashSuccess = flash?.success ?? flashNotice;
  const roleConfig = {
    patient: {
      label: 'Cliente',
      icon: User,
      forgotHref: '/patients/password/new',
    },
    therapist: {
      label: 'Psicologo',
      icon: Stethoscope,
      forgotHref: '/therapists/password/new',
    },
  } as const;
  const currentRole = roleConfig[role];
  const RoleIcon = currentRole.icon;

  return (
    <AuthLayout>
      <Head title="Accedi" />
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center gap-2 rounded-md border border-white/25 bg-white/20 px-4 py-3 text-white backdrop-blur">
          <RoleIcon className="h-4 w-4" />
          <span className="text-sm font-medium">Stai accedendo come {currentRole.label}</span>
        </div>
        <LoginForm
          onEmailLogin={(mail, pass) => {
            const payload =
              role === 'patient'
                ? { patient: { email: mail, password: pass } }
                : { therapist: { email: mail, password: pass } };
            router.post(submitUrl, payload, {
              preserveScroll: true,
              onStart: () => setIsSubmitting(true),
              onFinish: () => setIsSubmitting(false),
            });
          }}
          isLoading={isSubmitting}
          submitLabel={`Accedi come ${currentRole.label}`}
          forgotHref={currentRole.forgotHref}
          flashAlert={authError}
          flashNotice={flashSuccess}
          defaultEmail={props.lastEmail ?? ''}
        />
      </div>
    </AuthLayout>
  );
}
